import { Switch } from "@/components/shadcn/switch";
import { Skeleton } from "@/components/shadcn/skeleton";
import {
  Trash,
  BookOpen,
  Edit,
  TriangleAlert,
  ArrowUpRight,
  ExternalLink,
  EyeOff,
} from "lucide-react";
import { AddNewEntry, Tool } from "./AssistantDetailsContent";
import { useMemo, useState } from "react";
import {
  KnowledgeBaseQaSettingsInput,
  KnowledgeBaseV2WithTags,
} from "@/generated/types.gen";
import { KnowledgeBasesV2Service } from "@/generated";
import { KnowledgeBasePickerModal } from "./KnowledgeBasePickerModal";
import { NoxusAlert } from "../UI/NoxusAlert";
import { NoxusMenu } from "../UI/NoxusMenu";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { NoxusIcon } from "../UI/NoxusIcon";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";

import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useQuery } from "@tanstack/react-query";
import { SpotflowService } from "@/services/Spotflow/spotflow";
import { useSpotUser } from "@/utils/spotUser";
import { useDisclosure } from "@/utils/useDisclosure";
import { EmbeddingsWarningBanner } from "../Dialogs/CTADialog";
import Link from "next/link";
import { Button } from "../shadcn/button";
import { ExpandableTextarea } from "./ExpandableTextarea";
import { RichTextValue } from "@/components/Form/RichTextField";
import { useRouter } from "next/navigation";
import { IngestedBadge } from "../KnowledgeBases/IngestedBadge";
import { UnavailableResourceBadge } from "./UnavailableResourceBadge";

export const KnowledgeBasesField = ({
  tools,
  onToolsChange,
  isNodeConfig,
  isDisabled,
  itemConfig,
}: {
  tools: Tool[] | undefined;
  onToolsChange: (tools: Tool[]) => void;
  isNodeConfig?: boolean;
  isDisabled?: boolean;
  itemConfig?: Record<string, NoxusFormEntryConfig>;
}) => {
  const { spotGroup } = useSpotUser();
  const router = useRouter();
  const { data: embeddings, isLoading: embeddingsLoading } = useQuery({
    queryKey: [DATA_KEYS.EMBEDDINGS, spotGroup.id],
    queryFn: () =>
      SpotflowService.getModels("/models/embeddings", spotGroup.id),
  });

  const hasEmbeddings = useMemo(() => {
    if (!embeddings) return false;
    return Array.isArray(embeddings) && embeddings.length > 0;
  }, [embeddings]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const knowledgeBasesTools = useMemo(() => {
    return tools?.filter((tool) => tool.type === "kb_qa");
  }, [tools]) as KnowledgeBaseQaSettingsInput[] | undefined;

  const kbIds = useMemo(() => {
    return knowledgeBasesTools?.map((t) => t.kb_id).join(",") || "";
  }, [knowledgeBasesTools]);

  const {
    data: knowledgeBasesData,
    isLoading: isLoadingKBs,
    isSuccess: kbsLoaded,
  } = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2,
    {
      path: { group_id: spotGroup?.id },
      query: { ids: kbIds },
    },
    {
      staleTime: Infinity,
      enabled: !!kbIds,
    }
  );

  const { data: availabilityData } = useQuery({
    queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2, "availability", kbIds],
    queryFn: () =>
      KnowledgeBasesV2Service.getKnowledgeBasesV2({
        path: { group_id: spotGroup.id },
        query: {
          size: 1,
          page: 1,
          ignore_ids: kbIds || undefined,
        },
      }),
    staleTime: 30_000,
    enabled: !!spotGroup?.id,
  });
  const remainingKbsCount = availabilityData?.data?.total ?? null;

  const onChange = (tool?: Tool, enabled?: boolean) => {
    if (isDisabled) return;
    if (!tool) {
      return;
    }

    const newTools = tools?.map((t) => {
      if (
        (t as KnowledgeBaseQaSettingsInput)?.kb_id ===
        (tool as KnowledgeBaseQaSettingsInput).kb_id
      ) {
        return { ...t, enabled };
      }
      return t;
    });

    if (newTools) {
      onToolsChange(newTools);
    }
  };

  const onAddKnowledgeBase = (props: {
    knowledgeBaseId: string;
    config: Record<string, unknown>;
  }) => {
    const { knowledgeBaseId, config } = props;
    if (isDisabled) return;
    if (editingTool) {
      onToolsChange(
        tools?.map((tool) => {
          if (
            (tool as KnowledgeBaseQaSettingsInput)?.kb_id === editingTool.kb_id
          ) {
            return {
              ...tool,
              ...config,
            };
          }
          return tool;
        }) || []
      );
    } else {
      onToolsChange([
        ...(tools || []),
        {
          name: `kb_qa_${knowledgeBaseId}`,
          type: "kb_qa",
          enabled: true,
          kb_id: knowledgeBaseId,
          ...config,
        } as KnowledgeBaseQaSettingsInput,
      ]);
    }
  };

  const onDelete = (tool: KnowledgeBaseQaSettingsInput) => {
    if (isDisabled) return;
    if (tools) {
      onToolsChange(
        tools.filter(
          (t) => (t as KnowledgeBaseQaSettingsInput)?.kb_id !== tool.kb_id
        )
      );
    }
  };

  const [editingTool, setEditingTool] =
    useState<KnowledgeBaseQaSettingsInput | null>(null);

  const onEdit = (tool: KnowledgeBaseQaSettingsInput) => {
    if (isDisabled) return;
    setEditingTool(tool);
    onOpen();
  };

  const activeKnowledgeBases = useMemo(() => {
    return knowledgeBasesTools?.map((tool) => tool.kb_id);
  }, [knowledgeBasesTools]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-sm font-medium text-foreground leading-none">
            Knowledge
          </p>
          <p className="text-xs font-regular text-muted-foreground mt-1.5">
            Select the knowledge bases this agent can use.
          </p>
        </div>
        {!hasEmbeddings && !embeddingsLoading ? (
          <div className="mt-4">
            <EmbeddingsWarningBanner
              variant="warning"
              icon={<TriangleAlert />}
            />
            <Link
              href="/settings/platform/models?tab=providers"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full mt-4"
            >
              <Button variant="outline" className="w-full mt-4">
                <ArrowUpRight fontSize="0.75rem" />
                Go to Providers Page
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {knowledgeBasesTools?.map((tool) => {
              const kbData = knowledgeBasesData?.items?.find(
                (kb) => kb.id === tool.kb_id
              );
              const isMissing = kbsLoaded && !kbData;
              return (
                <KnowledgeEntry
                  key={tool.kb_id}
                  tool={tool}
                  kbData={kbData}
                  isMissing={isMissing}
                  isLoadingData={isLoadingKBs}
                  onChange={(enabled) => {
                    onChange(tool, enabled);
                  }}
                  onDelete={() => {
                    onDelete(tool);
                  }}
                  onEdit={() => {
                    onEdit(tool);
                  }}
                  onExtraInstructionsChange={(value) => {
                    if (isDisabled) return;
                    const newTools = tools?.map((t) => {
                      if (
                        (t as KnowledgeBaseQaSettingsInput)?.kb_id ===
                        tool.kb_id
                      ) {
                        return { ...t, extra_instructions: value };
                      }
                      return t;
                    });
                    if (newTools) {
                      onToolsChange(newTools as Tool[]);
                    }
                  }}
                  isDisabled={isDisabled}
                  fieldConfig={itemConfig?.["extra_instructions"]}
                />
              );
            })}

            {(knowledgeBasesTools?.length ?? 0) === 0 &&
            remainingKbsCount === 0 ? (
              <NoxusAlert
                icon={BookOpen}
                description="No knowledge bases available."
                action={{
                  label: (
                    <>
                      <ExternalLink />
                      Open knowledge bases
                    </>
                  ),
                  onClick: () => router.push("/?tab=knowledge-bases"),
                }}
              />
            ) : (
              <AddNewEntry
                label="Add Knowledge Base"
                disabledLabel={
                  remainingKbsCount === 0
                    ? "No more knowledge bases to add"
                    : undefined
                }
                onClick={onOpen}
                isDisabled={isDisabled || remainingKbsCount === 0}
              />
            )}
          </>
        )}
      </div>
      {isOpen && (
        <KnowledgeBasePickerModal
          isOpen={isOpen}
          onClose={() => {
            setEditingTool(null);
            onClose();
          }}
          onSelectKnowledgeBase={onAddKnowledgeBase}
          filteredKnowledgeBases={activeKnowledgeBases}
          editingTool={editingTool || undefined}
          itemConfig={itemConfig}
        />
      )}
    </div>
  );
};

const KnowledgeEntry = ({
  tool,
  kbData,
  isMissing,
  isLoadingData,
  onChange,
  onDelete,
  onEdit,
  onExtraInstructionsChange,
  isDisabled,
  fieldConfig,
}: {
  tool: KnowledgeBaseQaSettingsInput;
  kbData?: KnowledgeBaseV2WithTags;
  isMissing?: boolean;
  isLoadingData?: boolean;
  onChange: (enabled: boolean) => void;
  onDelete: () => void;
  onEdit: () => void;
  onExtraInstructionsChange: (value: RichTextValue) => void;
  isDisabled?: boolean;
  fieldConfig?: NoxusFormEntryConfig;
}) => {
  const { name } = kbData || {};
  const icon = (kbData?.meta?.icon as string) || "kb";
  const router = useRouter();
  return (
    <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <NoxusIcon icon={icon} />
          <div className="flex-1 min-w-0">
            {isLoadingData ? (
              <Skeleton className="h-5 w-32 mb-1" />
            ) : isMissing ? (
              <div className="flex items-center gap-1.5">
                <p className="font-sm text-sm text-muted-foreground truncate">
                  {name || "Deleted knowledge base"}
                </p>
                <UnavailableResourceBadge kind="knowledge base" />
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <NoxusTooltip content="Open in new tab" placement="top" asChild>
                  <a
                    href={`/data-hub/${tool.kb_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    <p className="font-sm text-sm">{name}</p>
                  </a>
                </NoxusTooltip>
                {tool.private && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                    <EyeOff className="h-3 w-3" />
                    Private
                  </span>
                )}
                {kbData && (kbData.num_docs ?? 0) > 0 && (
                  <IngestedBadge
                    ingested={kbData.trained_documents ?? 0}
                    total={kbData.num_docs ?? 0}
                    variant="dot"
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={tool.enabled}
            onCheckedChange={(checked) => onChange(checked)}
            disabled={isDisabled || isMissing}
          />
          <NoxusMenu
            isSmall
            itemsArray={[
              {
                label: "Edit",
                icon: Edit,
                onClick: onEdit,
                isHidden: isMissing,
              },
              {
                label: "Open in new tab",
                icon: ExternalLink,
                onClick: () => {
                  router.push(`/data-hub/${tool.kb_id}`);
                },
                isHidden: isMissing,
              },
              {
                label: "Remove",
                icon: Trash,
                onClick: onDelete,
                color: "destructive",
              },
            ]}
            isDisabled={isDisabled}
          />
        </div>
      </div>

      {tool.enabled && !isMissing && (
        <ExpandableTextarea
          value={tool.extra_instructions || ""}
          onChange={(value) => onExtraInstructionsChange(value)}
          isDisabled={isDisabled}
          label="Knowledge base usage conditions"
          placeholder="Specify when and how this knowledge base should be used..."
          fieldConfig={fieldConfig}
        />
      )}
    </div>
  );
};
